import React from 'react';
import { useAuthentication } from '@features/auth/useAuthentication.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCalendar,
  faCalendarAlt,
  faChartBar,
  faCheckCircle,
  faCog,
  faCrown,
  faDollarSign,
  faEdit,
  faEnvelope,
  faKey,
  faPhone,
  faShieldAlt,
  faSignOutAlt,
  faStore,
  faTimesCircle,
  faUser,
  faUserPlus,
  faUsers,
  faUserTie,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';

const ProfilePage: React.FC = () => {
  const { loggedUser, logout } = useAuthentication();

  if (!loggedUser) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Uživatel není přihlášen.</p>
        </div>
      </div>
    );
  }

  const access = loggedUser.accessRights;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleIcon = (role?: string) => {
    switch (role) {
      case 'OWNER':
        return faCrown;
      case 'RECEPTION':
        return faUserTie;
      case 'WORKER':
        return faShieldAlt;
      default:
        return faUser;
    }
  };

  const getRoleColor = (role?: string) => {
    switch (role) {
      case 'OWNER':
        return 'text-yellow-600 bg-yellow-100';
      case 'RECEPTION':
        return 'text-blue-600 bg-blue-100';
      case 'WORKER':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'OWNER':
        return 'Vlastník';
      case 'RECEPTION':
        return 'Recepce';
      case 'WORKER':
        return 'Pracovník';
      default:
        return role || 'Neznámá role';
    }
  };

  const getPermissionIcon = (permission: string) => {
    switch (permission) {
      case 'MANAGE_SALON':
        return faBuilding;
      case 'MANAGE_SERVICES':
        return faStore;
      case 'MANAGE_STAFF':
        return faUsers;
      case 'MANAGE_CUSTOMERS':
        return faUserPlus;
      case 'MANAGE_BOOKINGS':
        return faCalendar;
      case 'VIEW_REPORTS':
        return faChartBar;
      case 'MANAGE_FINANCES':
        return faDollarSign;
      case 'INVITE_USERS':
        return faUserPlus;
      case 'MANAGE_SETTINGS':
        return faCog;
      case 'MANAGE_SCHEDULE':
      case 'MANAGE_OWN_SCHEDULE':
        return faCalendarAlt;
      default:
        return faWrench;
    }
  };

  const translatePermission = (permission: string) => {
    const translations: Record<string, string> = {
      MANAGE_SALON: 'Spravovat salon',
      MANAGE_STAFF: 'Spravovat zaměstnance',
      MANAGE_CUSTOMERS: 'Spravovat zákazníky',
      MANAGE_BOOKINGS: 'Spravovat rezervace',
      VIEW_REPORTS: 'Zobrazit reporty',
      MANAGE_FINANCES: 'Spravovat finance',
      INVITE_USERS: 'Pozvat uživatele',
      MANAGE_SETTINGS: 'Spravovat nastavení',
      MANAGE_SERVICES: 'Spravovat služby',
      MANAGE_SCHEDULE: 'Spravovat rozvrh',
      MANAGE_OWN_SCHEDULE: 'Spravovat vlastní rozvrh',
    };
    return translations[permission] || permission;
  };

  const capabilityLabels: Array<{ key: keyof typeof access; label: string }> | [] = access
    ? [
        { key: 'canManageSalon' as const, label: 'Spravovat salon' },
        { key: 'canManageServices' as const, label: 'Spravovat služby' },
        { key: 'canManageStaff' as const, label: 'Spravovat zaměstnance' },
        { key: 'canManageCustomers' as const, label: 'Spravovat zákazníky' },
        { key: 'canManageBookings' as const, label: 'Spravovat rezervace' },
        { key: 'canViewReports' as const, label: 'Zobrazit reporty' },
        { key: 'canManageOwnSchedule' as const, label: 'Spravovat vlastní rozvrh' },
      ]
    : [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Můj profil</h1>
        <p className="text-gray-600">Spravujte své osobní informace a nastavení účtu</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="xl:col-span-3 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Osobní informace</h2>
              <button className="flex items-center text-green-600 hover:text-green-700 transition-colors">
                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                Upravit
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faUser} className="text-green-600 text-lg" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Jméno</p>
                  <p className="font-medium text-gray-900 text-lg">
                    {loggedUser.firstName} {loggedUser.lastName}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 text-lg" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">E-mail</p>
                  <div className="flex items-center">
                    <p className="font-medium text-gray-900 text-lg mr-2">{loggedUser.email}</p>
                    {loggedUser.emailVerified ? (
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" title="Ověřený e-mail" />
                    ) : (
                      <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" title="Neověřený e-mail" />
                    )}
                  </div>
                </div>
              </div>

              {/* Phone */}
              {loggedUser.phoneNumber && (
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <FontAwesomeIcon icon={faPhone} className="text-purple-600 text-lg" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="font-medium text-gray-900 text-lg">{loggedUser.phoneNumber}</p>
                  </div>
                </div>
              )}

              {/* Role */}
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={getRoleIcon(access?.role)} className="text-orange-600 text-lg" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Role</p>
                  <p className="font-medium text-gray-900 text-lg">{getRoleLabel(access?.role)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Salon access */}
          {access && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Salon</h2>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <FontAwesomeIcon icon={faStore} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{access.salonName}</h3>
                      <p className="text-sm text-gray-500">ID: {access.salonId}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(access.role)}`}>
                    <FontAwesomeIcon icon={getRoleIcon(access.role)} className="mr-1" />
                    {getRoleLabel(access.role)}
                  </span>
                </div>

                {/* Permissions */}
                {access.permissions && access.permissions.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Oprávnění:</h4>
                    <div className="flex flex-wrap gap-2">
                      {access.permissions.map(permission => (
                        <span key={permission} className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          <FontAwesomeIcon icon={getPermissionIcon(permission)} className="mr-1" />
                          {translatePermission(permission)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Capabilities */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Možnosti:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {capabilityLabels.map(({ key, label }) => {
                      const value = (access as any)[key] as boolean;
                      return (
                        <div key={key} className="flex items-center text-xs">
                          <FontAwesomeIcon icon={value ? faCheckCircle : faTimesCircle} className={`mr-1 ${value ? 'text-green-500' : 'text-red-500'}`} />
                          <span className={value ? 'text-green-700' : 'text-red-700'}>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Account Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Detaily účtu</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Datum vytvoření</p>
                <p className="font-medium text-gray-900">{formatDate(loggedUser.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Poslední aktualizace</p>
                <p className="font-medium text-gray-900">{formatDate(loggedUser.updatedAt)}</p>
              </div>
              {loggedUser.lastLogin ? (
                <div>
                  <p className="text-sm text-gray-500">Poslední přihlášení</p>
                  <p className="font-medium text-gray-900">{formatDate(loggedUser.lastLogin)}</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500">Poslední přihlášení</p>
                  <p className="font-medium text-gray-500">Nikdy</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rychlé akce</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                Upravit profil
              </button>
              <button className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <FontAwesomeIcon icon={faKey} className="mr-2" />
                Změnit heslo
              </button>
              <button onClick={logout} className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                Odhlásit se
              </button>
            </div>
          </div>

          {/* Account Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Stav účtu</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">E-mail ověřen</span>
                <span className={`text-xs px-2 py-1 rounded-full ${loggedUser.emailVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {loggedUser.emailVerified ? 'Ano' : 'Ne'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Aktivní účet</span>
                <span className={`text-xs px-2 py-1 rounded-full ${loggedUser.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {loggedUser.status === 'ACTIVE' ? 'Ano' : 'Ne'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">ID uživatele</span>
                <span className="text-xs text-gray-500 font-mono">{loggedUser.id.slice(0, 8)}...</span>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Přehled</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Salon</span>
                <span className="text-sm font-medium text-gray-900">{access ? 1 : 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Oprávnění</span>
                <span className="text-sm font-medium text-gray-900">{access?.permissions?.length || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
